import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  roadsqueeze,
  carbounce,
  rotate3,
  rotate2,
  rotate1,
  wheelshiftforward,
  wheelshiftbackward,
} from '../shared-styles';

export const StyledCatCarSvg = styled.svg`
  --global-iteration-count: ${({ isAnimated }) => (isAnimated ? 'infinite' : 0)};
  --global-duration: 1.5s;
  --global-timing-function: cubic-bezier(0.68, -0.6, 0.32, 1.6);
  --color-gray-50: ${({ theme }) => theme.color.gray[50].value};
  --color-gray-100: ${({ theme }) => theme.color.gray[100].value};
  --color-gray-200: ${({ theme }) => theme.color.gray[200].value};
  --color-gray-300: ${({ theme }) => theme.color.gray[300].value};
  --color-gray-400: ${({ theme }) => theme.color.gray[400].value};
  --color-gray-500: ${({ theme }) => theme.color.gray[500].value};
  --color-gray-600: ${({ theme }) => theme.color.gray[600].value};
  --color-gray-700: ${({ theme }) => theme.color.gray[700].value};
  --color-gray-800: ${({ theme }) => theme.color.gray[800].value};
  --color-gray-900: ${({ theme }) => theme.color.gray[900].value};

  .car-body,
  .cat-1,
  .cat-2,
  .front-detail,
  .map,
  .luggage {
    animation-name: ${carbounce};
  }

  .box-1 {
    animation-name: ${rotate1};
  }

  .box-2 {
    animation-name: ${rotate2};
  }

  .box-3 {
    animation-name: ${rotate3};
  }

  .road {
    animation-name: ${roadsqueeze};
  }

  .wheel-front-l,
  .wheel-front-r {
    animation-name: ${wheelshiftforward};
  }

  .wheel-back-l,
  .wheel-back-r {
    animation-name: ${wheelshiftbackward};
  }

  .wheel-face {
    fill: var(--color-gray-800);
  }

  .box-1,
  .box-2,
  .box-3,
  .car-body,
  .cat-1,
  .cat-2,
  .front-detail,
  .luggage,
  .map,
  .road,
  .wheel-back-l,
  .wheel-back-r,
  .wheel-front-l,
  .wheel-front-r {
    animation-duration: var(--global-duration);
    animation-iteration-count: var(--global-iteration-count);
    transition-timing-function: var(--global-timing-function);
  }
`;

const LostCatCar = ({ className, dataTestId, isAnimated = true, size = 360, ...other }) => {
  return (
    <StyledCatCarSvg
      className={className}
      data-testid={dataTestId ? `${dataTestId}-cat-car-svg` : undefined}
      fill="none"
      height={size}
      isAnimated={isAnimated}
      viewBox="0 0 360 275"
      width={size}
      xmlns="http://www.w3.org/2000/StyledCatCarSvg"
    >
      <title id="lost-cat-car-svg">Lost cat car animated svg</title>
      <g className="lost-cats">
        <g className="wheels-left">
          <g className="wheel-back-l">
            <path
              className="wheel-tread"
              d="M156.536 254.321C156.536 262.132 150.204 268.464 142.393 268.464C141.401 268.464 139.179 268.464 138.214 268.464C131.791 267.129 128.25 261.141 128.25 254.321C128.25 247.616 131.952 241.634 138.214 240.178C139.5 240.178 141.287 240.178 142.393 240.178C150.204 240.178 156.536 246.51 156.536 254.321Z"
              fill="var(--color-gray-900)"
            />
            <circle className="wheel-face" cx="138.536" cy="254.321" r="14.1429" />
          </g>
          <g className="wheel-front-l">
            <path
              className="wheel-tread-2"
              d="M314.679 254.321C314.679 262.132 308.347 268.464 300.536 268.464C299.545 268.464 297.322 268.464 296.357 268.464C289.934 267.129 286.393 261.141 286.393 254.321C286.393 247.616 290.095 241.634 296.357 240.178C297.643 240.178 299.43 240.178 300.536 240.178C308.347 240.178 314.679 246.51 314.679 254.321Z"
              fill="var(--color-gray-900)"
            />
            <circle className="wheel-face-2" cx="296.679" cy="254.321" r="14.1429" fill="var(--color-gray-800)" />
          </g>
        </g>
        <rect className="road" y="267" width="360" height="8" rx="4" fill="var(--color-gray-300)" />
        <g className="car-body">
          <path
            className="body-front"
            d="M104.464 150.525C104.464 139.432 104.464 133.885 105.966 129.409C108.849 120.811 115.597 114.063 124.195 111.179C128.671 109.678 134.218 109.678 145.311 109.678H202.179C203.969 109.678 204.865 109.678 205.622 109.686C283.256 110.51 345.989 173.244 346.813 250.878C346.822 251.635 346.822 252.53 346.822 254.321V254.321H104.464V150.525Z"
            fill="var(--color-gray-600)"
          />
          <path
            className="body-side"
            d="M13.1787 223.386C13.1787 211.518 13.1787 205.584 13.6805 200.587C18.4463 153.126 55.9832 115.589 103.445 110.823C108.442 110.321 114.376 110.321 126.243 110.321H140.027C142.821 110.321 144.217 110.321 145.399 110.342C216.332 111.614 273.528 168.811 274.8 239.744C274.822 240.925 274.822 242.322 274.822 245.116V245.116C274.822 245.306 274.822 245.402 274.82 245.482C274.733 250.327 270.827 254.233 265.983 254.32C265.902 254.321 265.807 254.321 265.616 254.321H25.0375C22.3793 254.321 21.0502 254.321 19.9566 254.042C16.7639 253.229 14.2708 250.736 13.4573 247.543C13.1787 246.45 13.1787 245.12 13.1787 242.462V223.386Z"
            fill="var(--color-gray-500)"
          />
          <path
            className="window-front"
            d="M226.065 125.799C227.993 119.369 269.779 128.048 298.386 160.191C326.993 192.334 334.708 218.37 332.779 222.227C330.851 226.083 293.886 225.762 290.672 222.227C287.458 218.692 288.422 198.764 269.458 169.513C250.493 140.262 224.136 132.229 226.065 125.799Z"
            fill="var(--color-gray-100)"
          />
          <path
            className="window-side"
            d="M27.9644 216.367C27.9644 165.242 69.41 123.179 120.536 123.179H145.929C145.929 123.179 189.643 123.822 223.072 153.714C248.542 176.491 257.031 203.186 259.63 214.605C260.311 217.6 257.987 220.25 254.916 220.25H31.8215C29.6913 220.25 27.9644 218.498 27.9644 216.367V216.367Z"
            fill="var(--color-gray-50)"
          />
        </g>
        <g className="luggage">
          <g className="box-3">
            <rect
              className="box-side"
              x="79.3931"
              y="71.1067"
              width="65.5714"
              height="39.2143"
              fill="var(--color-gray-300)"
            />
            <rect
              className="shadow"
              x="144.964"
              y="71.1067"
              width="39.2143"
              height="39.2143"
              fill="var(--color-gray-500)"
            />
          </g>
          <g className="box-2">
            <rect
              className="box-side-2"
              x="108.322"
              y="44.7498"
              width="65.5714"
              height="26.3571"
              fill="var(--color-gray-600)"
            />
            <path
              className="shadow-2"
              d="M173.893 44.7498H213.107V71.1069H173.893V44.7498Z"
              fill="var(--color-gray-900)"
            />
          </g>
          <g className="box-1">
            <rect
              className="box-side-3"
              x="84.5361"
              y="0.392578"
              width="47.5714"
              height="44.3571"
              fill="var(--color-gray-100)"
            />
            <rect
              className="shadow-3"
              x="132.107"
              y="0.392578"
              width="19.9286"
              height="44.3571"
              fill="var(--color-gray-900)"
            />
          </g>
        </g>
        <g className="wheels-right">
          <g className="wheel-back-r">
            <path
              className="wheel-tread-3"
              d="M79.3931 254.853C79.3931 262.664 73.0612 268.996 65.2503 268.996C64.2589 268.996 62.036 268.996 61.0717 268.996C54.6483 267.661 51.1074 261.672 51.1074 254.853C51.1074 248.148 54.8094 242.165 61.0717 240.71C62.3574 240.71 64.1446 240.71 65.2503 240.71C73.0612 240.71 79.3931 247.042 79.3931 254.853Z"
              fill="var(--color-gray-900)"
            />
            <circle className="wheel-face-3" cx="61.3929" cy="254.853" r="14.1429" fill="var(--color-gray-800)" />
            <circle className="hubcap" cx="61.3931" cy="254.852" r="5.78571" fill="var(--color-gray-50)" />
          </g>
          <g className="wheel-front-r">
            <path
              className="wheel-tread-4"
              d="M237.536 254.853C237.536 262.664 231.204 268.996 223.393 268.996C222.401 268.996 220.179 268.996 219.214 268.996C212.791 267.661 209.25 261.672 209.25 254.853C209.25 248.148 212.952 242.165 219.214 240.71C220.5 240.71 222.287 240.71 223.393 240.71C231.204 240.71 237.536 247.042 237.536 254.853Z"
              fill="var(--color-gray-900)"
            />
            <circle className="wheel-face-4" cx="219.535" cy="254.853" r="14.1429" fill="var(--color-gray-800)" />
            <circle className="hubcap-2" cx="219.536" cy="254.852" r="5.78571" fill="var(--color-gray-50)" />
          </g>
        </g>
        <g className="cats">
          <g className="cat-1">
            <path
              className="body-1"
              d="M92.838 178.108C92.8679 177.748 93.1935 177.48 93.5377 177.59C95.9952 178.378 96.7298 181.918 97.7672 183.752C97.9124 184.009 98.2314 184.095 98.5062 183.988C107.558 180.454 112.628 181.989 119.837 184.304C120.207 184.423 120.593 184.191 120.648 183.807C120.992 181.439 121.685 176.05 123.701 175.992C123.944 175.985 124.154 176.121 124.28 176.33C127.081 180.956 135.321 220.255 135.321 220.255H90.3213C90.3213 220.255 91.2637 197.092 92.838 178.108Z"
              fill="var(--color-gray-900)"
            />
            <ellipse
              className="eye-left-1"
              cx="99.9641"
              cy="191.956"
              rx="0.642857"
              ry="0.707464"
              fill="var(--color-gray-50)"
            />
            <ellipse
              className="eye-right-1"
              cx="119.893"
              cy="191.248"
              rx="0.642857"
              ry="0.707464"
              fill="var(--color-gray-50)"
            />
            <path
              className="nose-1"
              d="M110.25 193.65C110.25 194.277 109.962 194.786 109.607 194.786C109.252 194.786 108.964 194.277 108.964 193.65C108.964 193.023 109.446 193.65 109.607 193.65C109.767 193.65 110.25 193.023 110.25 193.65Z"
              fill="var(--color-gray-300)"
            />
            <line
              className="whisker-1"
              y1="-0.321429"
              x2="8.62244"
              y2="-0.321429"
              transform="matrix(-0.969232 -0.246148 0.205231 -0.978713 94.8213 191.957)"
              stroke="var(--color-gray-300)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-2"
              y1="-0.321429"
              x2="8.62244"
              y2="-0.321429"
              transform="matrix(-0.969232 0.246148 0.205231 0.978714 134.036 188.418)"
              stroke="var(--color-gray-300)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-3"
              y1="-0.321429"
              x2="8.47608"
              y2="-0.321429"
              transform="matrix(-0.985968 -0.166932 0.13845 -0.990369 94.8213 194.786)"
              stroke="var(--color-gray-300)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-4"
              y1="-0.321429"
              x2="8.47608"
              y2="-0.321429"
              transform="matrix(-0.985968 0.166932 0.13845 0.990369 134.036 191.957)"
              stroke="var(--color-gray-300)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-5"
              x1="94.8213"
              y1="197.937"
              x2="85.8213"
              y2="197.937"
              stroke="var(--color-gray-300)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-6"
              y1="-0.321429"
              x2="9"
              y2="-0.321429"
              transform="matrix(-1 0 0 1 134.036 196.201)"
              stroke="var(--color-gray-300)"
              strokeWidth="0.642857"
            />
          </g>
          <g className="cat-2">
            <path
              className="body-2"
              d="M168.584 157.677C168.683 157.423 168.934 157.304 169.199 157.368C171.765 157.989 171.43 161.258 172.431 163.028C172.532 163.207 172.725 163.304 172.931 163.3C175.413 163.248 182.062 162.712 188.326 164.197C188.691 164.283 189.059 164.052 189.172 163.695C189.782 161.764 191.637 158.455 193.775 158.639C194.018 158.659 194.223 158.781 194.325 159.002C196.76 164.313 197.678 220.253 197.678 220.253H155.893C155.893 220.253 165.765 164.912 168.584 157.677Z"
              fill="var(--color-gray-300)"
            />
            <ellipse
              className="eye-left-2"
              cx="170.035"
              cy="170.732"
              rx="0.642857"
              ry="0.707464"
              fill="var(--color-gray-50)"
            />
            <ellipse
              className="eye-right-2"
              cx="189.964"
              cy="172.854"
              rx="0.642857"
              ry="0.707464"
              fill="var(--color-gray-50)"
            />
            <path
              className="nose-2"
              d="M180.321 173.133C180.321 173.761 180.033 174.269 179.678 174.269C179.323 174.269 179.035 173.761 179.035 173.133C179.035 172.506 179.517 173.133 179.678 173.133C179.839 173.133 180.321 172.506 180.321 173.133Z"
              fill="var(--color-gray-900)"
            />
            <line
              className="whisker-1"
              y1="-0.321429"
              x2="8.62244"
              y2="-0.321429"
              transform="matrix(-0.969232 -0.246148 0.205231 -0.978713 166.178 170.024)"
              stroke="var(--color-gray-900)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-2"
              y1="-0.321429"
              x2="8.62244"
              y2="-0.321429"
              transform="matrix(-0.969232 0.246148 0.205231 0.978714 203.464 167.902)"
              stroke="var(--color-gray-900)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-3"
              y1="-0.321429"
              x2="8.47608"
              y2="-0.321429"
              transform="matrix(-0.985968 -0.166932 0.13845 -0.990369 166.178 172.854)"
              stroke="var(--color-gray-900)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-4"
              y1="-0.321429"
              x2="8.47608"
              y2="-0.321429"
              transform="matrix(-0.985968 0.166932 0.13845 0.990369 203.464 171.439)"
              stroke="var(--color-gray-900)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-5"
              x1="166.178"
              y1="176.006"
              x2="157.178"
              y2="176.006"
              stroke="var(--color-gray-900)"
              strokeWidth="0.642857"
            />
            <line
              className="whisker-6"
              y1="-0.321429"
              x2="9"
              y2="-0.321429"
              transform="matrix(-1 0 0 1 203.464 175.685)"
              stroke="var(--color-gray-900)"
              strokeWidth="0.642857"
            />
          </g>
        </g>
        <g className="front-detail">
          <circle className="headlightleft" cx="299.25" cy="236.964" r="2.57143" fill="var(--color-gray-300)" />
          <circle className="headlightright" cx="332.679" cy="236.964" r="2.57143" fill="var(--color-gray-300)" />
          <path
            className="grill"
            d="M305.679 236.964C305.679 236.609 305.967 236.321 306.322 236.321H326.192C326.579 236.321 326.893 236.635 326.893 237.022V237.022C326.893 238.765 325.48 240.178 323.737 240.178H308.893C307.118 240.178 305.679 238.739 305.679 236.964V236.964Z"
            fill="var(--color-gray-900)"
          />
        </g>
        <g className="map">
          <path
            className="map-2"
            d="M146.25 183.171C146.25 182.745 146.644 182.433 147.055 182.549C151.603 183.836 174.032 190.2 178.071 191.643C181.273 190.479 206.874 184.835 211.686 183.778C212.087 183.69 212.464 183.995 212.464 184.406V234.443C212.464 234.778 212.221 235.056 211.887 235.084C207.885 235.421 184.244 237.478 178.071 239.536C172.2 237.48 150.507 234.353 146.793 233.826C146.475 233.781 146.25 233.511 146.25 233.19V183.171Z"
            fill="var(--color-gray-100)"
          />
          <path
            className="map-right"
            d="M178.071 202.893C178.071 202.893 178.071 202.893 178.071 202.893C178.071 202.893 178.071 198.714 178.071 191.643C181.273 190.478 206.874 184.834 211.686 183.778C212.087 183.69 212.464 183.995 212.464 184.406V234.443C212.464 234.778 212.221 235.055 211.887 235.084C207.885 235.421 184.244 237.478 178.071 239.536V239.536C178.071 239.535 178.071 214.993 178.071 214.993L178.071 202.893Z"
            fill="var(--color-gray-200)"
          />
          <path
            className="map-line"
            d="M146.25 219.286L155.196 205.568C155.484 205.126 156.153 205.201 156.337 205.696L159.633 214.569C159.816 215.061 160.479 215.138 160.77 214.702L163.253 210.977C163.54 210.547 164.191 210.614 164.384 211.093L170.046 225.111C170.267 225.659 171.048 225.643 171.247 225.087L173.392 219.081C173.582 218.55 174.314 218.503 174.57 219.004L180.81 231.225C181.082 231.758 181.871 231.662 182.009 231.08L187.462 207.904C187.614 207.257 188.529 207.239 188.707 207.88L190.634 214.819C190.809 215.446 191.697 215.448 191.873 214.821L196.174 199.529C196.364 198.853 197.344 198.927 197.431 199.623L200.261 222.267C200.344 222.927 201.251 223.043 201.496 222.425L212.464 194.857"
            stroke="var(--color-gray-900)"
            strokeWidth="0.642857"
            strokeLinecap="round"
            strokeDasharray="2.57 2.57"
          />
        </g>
      </g>
    </StyledCatCarSvg>
  );
};

LostCatCar.propTypes = {
  /** Adds additional class to the component */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Toggles animation on and off. SVGs are animated by default */
  isAnimated: PropTypes.bool,
  /** Adjusts the height and width of the svg. The height and width ratio is locked to 1:1. Absolute and relative units optional */
  size: PropTypes.number,
};

export { LostCatCar };
